import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
    Loader2, PlayCircle, CheckCircle2, TrendingUp, TrendingDown,
    Minus, AlertTriangle, Zap, RefreshCw
} from 'lucide-react';
import { simulateExistingRule, applySimulation } from '@/services/api';
import type { PriceRule } from '@/types/price-rules';

interface SyncRuleModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    rule: PriceRule | null;
    onSuccess?: () => void;
}

interface SimulationResult {
    rule_id: number;
    rule?: any;
    domain_name?: string;
    current_price: number;
    metrics: {
        ecpm: number;
        pmr: number;
        impressions: number;
        revenue?: number;
        clicks?: number;
        ctr?: number;
    } | null;
    simulation: {
        direction: 'up' | 'down' | 'hold';
        pmr_atual: number;
        pmr_target: number;
        pmr_difference: number;
        scale_percentage: number;
        new_price: number;
        reason: string;
    } | null;
    has_data: boolean;
    can_apply: boolean;
    message: string;
}

interface ApplyResult {
    success: boolean;
    old_price?: number;
    new_price?: number;
    direction?: string;
    scale_percentage?: number;
    pmr_atual?: number;
    pmr_target?: number;
    ecpm?: number;
    message: string;
}

export default function SyncRuleModal({ open, onOpenChange, rule, onSuccess }: SyncRuleModalProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [simulation, setSimulation] = useState<SimulationResult | null>(null);
    const [forceSync, setForceSync] = useState(false);
    const [applied, setApplied] = useState(false);

    const simulateMutation = useMutation({
        mutationFn: () => simulateExistingRule(rule!.id),
        onSuccess: (data: SimulationResult) => {
            setSimulation(data);
            setApplied(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao simular',
                description: error.response?.data?.detail || 'Não foi possível simular a sincronização.',
            });
        },
    });

    const applyMutation = useMutation({
        mutationFn: () => applySimulation(rule!.id, forceSync),
        onSuccess: (data: ApplyResult) => {
            if (data.success) {
                toast({
                    title: 'Sincronização aplicada!',
                    description: data.message,
                });
                setApplied(true);
                queryClient.invalidateQueries({ queryKey: ['price-rules'] });
                onSuccess?.();
            } else {
                toast({
                    variant: 'destructive',
                    title: 'Não foi possível aplicar',
                    description: data.message,
                });
            }
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao aplicar',
                description: error.response?.data?.detail || 'Não foi possível aplicar a sincronização.',
            });
        },
    });

    const handleClose = () => {
        setSimulation(null);
        setApplied(false);
        setForceSync(false);
        onOpenChange(false);
    };

    const formatPrice = (price: number) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD' }).format(price);

    const getDirectionIcon = (direction: string) => {
        switch (direction) {
            case 'up': return <TrendingUp className="h-6 w-6 text-green-600" />;
            case 'down': return <TrendingDown className="h-6 w-6 text-red-600" />;
            default: return <Minus className="h-6 w-6 text-gray-500" />;
        }
    };

    const getDirectionLabel = (direction: string) => {
        switch (direction) {
            case 'up': return 'Aumentar';
            case 'down': return 'Reduzir';
            default: return 'Manter';
        }
    };

    const getDirectionColor = (direction: string) => {
        switch (direction) {
            case 'up': return 'text-green-700 bg-green-50 border-green-200';
            case 'down': return 'text-red-700 bg-red-50 border-red-200';
            default: return 'text-gray-700 bg-gray-50 border-gray-200';
        }
    };

    if (!rule) return null;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Zap className="h-5 w-5" />
                        Sincronizar Regra
                    </DialogTitle>
                    <DialogDescription>
                        Simule e aplique a sincronização de preço baseada no PMR atual.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                    {/* Rule Info */}
                    <div className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-900">
                                    {rule.country === '*' ? 'Todos países' : rule.country} • {rule.ad_unit === '*' ? 'Todos blocos' : rule.ad_unit}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    {rule.device} {rule.custom_key && `• ${rule.custom_key}=${rule.custom_value}`}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-lg font-bold text-gray-900">{formatPrice(rule.price)}</p>
                                <p className="text-xs text-gray-500">Price atual</p>
                            </div>
                        </div>
                    </div>

                    {/* Simulate Button */}
                    {!simulation && !simulateMutation.isPending && (
                        <Button
                            onClick={() => simulateMutation.mutate()}
                            className="w-full"
                            size="lg"
                        >
                            <PlayCircle className="mr-2 h-5 w-5" />
                            Simular Sincronização
                        </Button>
                    )}

                    {/* Loading */}
                    {simulateMutation.isPending && (
                        <div className="flex flex-col items-center justify-center py-8">
                            <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
                            <p className="mt-3 text-gray-600">Consultando GAM...</p>
                        </div>
                    )}

                    {/* Simulation Result */}
                    {simulation && (
                        <div className="space-y-4">
                            {/* Metrics */}
                            {simulation.metrics && (
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="p-3 bg-white border rounded-lg text-center">
                                        <p className="text-xs text-gray-500">eCPM</p>
                                        <p className="text-lg font-bold">{formatPrice(simulation.metrics.ecpm)}</p>
                                    </div>
                                    <div className="p-3 bg-white border rounded-lg text-center">
                                        <p className="text-xs text-gray-500">PMR</p>
                                        <p className="text-lg font-bold">{simulation.metrics.pmr.toFixed(1)}%</p>
                                    </div>
                                    <div className="p-3 bg-white border rounded-lg text-center">
                                        <p className="text-xs text-gray-500">Impressões</p>
                                        <p className="text-lg font-bold">{simulation.metrics.impressions.toLocaleString()}</p>
                                    </div>
                                </div>
                            )}

                            {/* No Data */}
                            {!simulation.has_data && (
                                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                                        <p className="text-sm text-yellow-800">{simulation.message}</p>
                                    </div>
                                </div>
                            )}

                            {/* Simulation Info */}
                            {simulation.simulation && (
                                <div className={`p-4 rounded-lg border-2 ${getDirectionColor(simulation.simulation.direction)}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            {getDirectionIcon(simulation.simulation.direction)}
                                            <div>
                                                <p className="font-semibold">
                                                    {getDirectionLabel(simulation.simulation.direction)} {simulation.simulation.scale_percentage > 0 ? `${simulation.simulation.scale_percentage}%` : ''}
                                                </p>
                                                <p className="text-sm opacity-80">{simulation.simulation.reason}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {simulation.simulation.direction !== 'hold' && (
                                        <div className="mt-4 flex items-center justify-between pt-3 border-t border-current border-opacity-20">
                                            <div>
                                                <p className="text-sm opacity-70">Price atual</p>
                                                <p className="text-xl font-bold">{formatPrice(simulation.current_price)}</p>
                                            </div>
                                            <div className="text-2xl">→</div>
                                            <div className="text-right">
                                                <p className="text-sm opacity-70">Novo price</p>
                                                <p className="text-xl font-bold">{formatPrice(simulation.simulation.new_price)}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-3 pt-3 border-t border-current border-opacity-20 text-sm">
                                        <div className="flex justify-between">
                                            <span className="opacity-70">PMR Atual:</span>
                                            <span className="font-medium">{simulation.simulation.pmr_atual.toFixed(1)}%</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="opacity-70">Meta:</span>
                                            <span className="font-medium">{simulation.simulation.pmr_target}%</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="opacity-70">Diferença:</span>
                                            <span className="font-medium">{simulation.simulation.pmr_difference.toFixed(1)}%</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Applied Success */}
                            {applied && (
                                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                                        <p className="text-sm text-green-800 font-medium">Sincronização aplicada com sucesso!</p>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            {!applied && (
                                <div className="space-y-3 pt-2">
                                    {/* Force Sync Toggle */}
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div>
                                            <Label className="text-sm font-medium">Forçar sincronização</Label>
                                            <p className="text-xs text-gray-500">Ignora guards e aplica mesmo assim</p>
                                        </div>
                                        <Switch checked={forceSync} onCheckedChange={setForceSync} />
                                    </div>

                                    <div className="flex gap-3">
                                        <Button
                                            variant="outline"
                                            onClick={() => simulateMutation.mutate()}
                                            disabled={simulateMutation.isPending || applyMutation.isPending}
                                            className="flex-1"
                                        >
                                            <RefreshCw className="mr-2 h-4 w-4" />
                                            Simular Novamente
                                        </Button>

                                        <Button
                                            onClick={() => applyMutation.mutate()}
                                            disabled={!simulation.can_apply || applyMutation.isPending}
                                            className="flex-1"
                                        >
                                            {applyMutation.isPending ? (
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            ) : (
                                                <CheckCircle2 className="mr-2 h-4 w-4" />
                                            )}
                                            Aplicar
                                        </Button>
                                    </div>

                                    {!simulation.can_apply && simulation.has_data && (
                                        <p className="text-xs text-center text-gray-500">
                                            Não há alteração para aplicar (preço se mantém igual)
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Close after applied */}
                            {applied && (
                                <Button onClick={handleClose} className="w-full">
                                    Fechar
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}